import { useEffect, useRef, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { ArrowUpRight } from 'lucide-react';
import { fromCardBackground } from '../../../lib/card-background-click';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { connectorsPath } from '../../../lib/connectors-route';
import { sortConnectors, useAppsController, type AppsController } from '../../../lib/use-connectors';
import { connectorDescriptionKey, connectorDisplayName, type ConnectorStatus } from '../../../lib/connectors';
import { homeConnectorGroups } from '../../../lib/home-connector-groups';
import { useAlfredInOutreachEnabled } from '../../../lib/alfred-in-outreach-flag';
import { ConnectorLogo } from '../connectors/ConnectorLogo';
import { Button, Card, TextButton, Typography } from '../ui';
import { HOME3_PAGE } from './Home3Feed';
import styles from './Home3ConnectorsRail.module.css';

/**
 * **Recommended connectors, at the foot of the Overview home's "All" view** — one white card across the
 * page's full width, its rows side by side, with a link to the connectors screen. A category page shows
 * none, and nothing already connected is repeated here (the Connectors screen lists it). Both phones draw
 * the same rule: recommended only, under "All" only (`ActivityMapListHome`).
 *
 * It is the same data and the same controller as `Home2ConnectorsSection`, which keeps its own shape on
 * the Home 2 bento.
 *
 * ## The rows take a tap but show no hover
 *
 * Design asked for no hover fill on these rows (Monday 13258431675), so the row Cards are
 * `interactive="press"`: the whole row is still the hit target (#11579) and still shows it is pressed.
 * Only the hover is gone — the fill, and the browser tooltip a `title` on the name or blurb would pop.
 */

/** How many recommendations the card holds. Past this the card's link is the way through. */
const RECOMMENDED_COUNT = 3;

function useDescription(connector: ConnectorStatus): string {
  const { t } = useTranslation();
  const productName = useProductName();
  // Prefer the localized `connectors.desc.<slug>` blurb, fall back to whatever the server sent, and
  // never show the key.
  const key = connectorDescriptionKey(connector);
  const localized = key ? t(key, { productName }) : '';
  return localized && localized !== key ? localized : connector.description || '';
}

function RecommendedRow({ connector, controller }: { connector: ConnectorStatus; controller: AppsController }) {
  const { t } = useTranslation();
  const name = connectorDisplayName(connector);
  const description = useDescription(connector);
  // Per ROW, never "is anything in flight": a connect is an OAuth popup the user drives, so the other
  // recommended rows stay pressable while it is open (see `ConnectConnector.busy`).
  const idle = !controller.busy.has(connector.toolkit);

  return (
    <Card
      disablePadding
      interactive={idle ? 'press' : undefined}
      className={`${styles.row} ${styles.recRow}${idle ? ` ${styles.clickable}` : ''}`}
      onClick={fromCardBackground(() => {
        if (idle) controller.connect(connector);
      })}
    >
      <ConnectorLogo connector={connector} name={name} size={28} radius={8} glyphSize={16} />
      <span className={`${styles.rowInfo} ${styles.recInfo}`}>
        <Typography variant="h4" color="primary" component="span" noWrap>
          {name}
        </Typography>
        {description && (
          <Typography variant="p3" color="secondary" component="span" className={styles.rowDesc}>
            {description}
          </Typography>
        )}
      </span>
      <Button
        size="small"
        variant="outlined"
        onClick={() => controller.connect(connector)}
        loading={!idle}
      >
        {t('common.connect')}
      </Button>
    </Card>
  );
}

function Group({
  labelKey,
  linkKey,
  biName,
  count,
  children,
}: {
  labelKey: string;
  linkKey: string;
  biName: string;
  count: number;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <Card className={styles.group}>
      <div className={styles.groupHead}>
        {/* **The count is what is SHOWN, not the size of the group** — the catalog is 66 connectors
            long, and "Recommended · 66" over three rows is not a backlog the reader has. */}
        <Typography variant="display-05" color="primary" component="h3" className={styles.groupTitle}>
          {t('home.overview.connectors.group', { label: t(labelKey), count })}
        </Typography>
        {/* The path is BARE — the DS `TextButton` prefixes the active mount itself, and a prefixed href
            here produced `/orion-web/orion-web/…`. */}
        <TextButton
          size="small"
          href={connectorsPath({ tab: 'tools' })}
          endIcon={<ArrowUpRight size={16} aria-hidden className="dir-flip" />}
          onClick={() => logPageClickBi({ page_name: HOME3_PAGE, button_name: biName })}
        >
          {t(linkKey)}
        </TextButton>
      </div>
      <div className={styles.rows}>{children}</div>
    </Card>
  );
}

export function Home3ConnectorsRail() {
  const { t } = useTranslation();
  const router = useRouter();
  const controller = useAppsController();
  const alfredOnOutreach = useAlfredInOutreachEnabled();

  // The home never remounts (DashboardHost keeps it alive) and connectors are local state with no
  // shared cache, so a connect/disconnect done on the connectors screen would not otherwise reach this
  // rail. Re-read whenever the reader navigates BACK to the home route. Same reason, same fix, as
  // `Home2ConnectorsSection`.
  const refreshRef = useRef(controller.refresh);
  refreshRef.current = controller.refresh;
  const wasHomeRef = useRef(router.pathname === '/');
  useEffect(() => {
    const isHome = router.pathname === '/';
    if (isHome && !wasHomeRef.current) void refreshRef.current();
    wasHomeRef.current = isHome;
  }, [router.pathname]);

  const state = controller.state;
  // It sits at the foot of the page, under everything else, so a late read moves nothing above it.
  if (state.kind !== 'ready' || !state.enabled) return null;

  // Recommended only: what is already connected is not repeated on the home, the Connectors screen lists it.
  const recommended = sortConnectors(homeConnectorGroups(state.connectors, alfredOnOutreach).recommended).slice(0, RECOMMENDED_COUNT);
  if (recommended.length === 0) return null;

  return (
    <section aria-label={t('home2.connectors.title')} className={styles.band}>
      {recommended.length > 0 && (
        <Group
          labelKey="home2.connectors.recommended"
          linkKey="connectors.viewAll"
          biName="connectors_view_all"
          count={recommended.length}
        >
          {recommended.map((c) => (
            <RecommendedRow key={c.toolkit} connector={c} controller={controller} />
          ))}
        </Group>
      )}

    </section>
  );
}
